// The authorization predicates — ADMIN_PANEL_ARCHITECTURE_TEMPLATE.md §8.
//
// ⚠ ALL OF THIS IS COSMETIC. Template §8, verbatim: "The UI hides what you cannot do; the API
// enforces it… Anyone can edit the JS bundle in DevTools and render whatever they like. The backend
// must independently enforce every one of these rules on every request."
//
// It does. Every predicate below has a server-side counterpart that is the real gate:
//   • the router's `requireDesignation` chain (`functions/src/routers/reportRoutes.js`);
//   • `reportService.runReport`, which re-checks the catalogue row's own `roles`;
//   • `reportService.scopeMatchFor`, which narrows the query to the caller's department or
//     assignments and REFUSES rather than widening when a source cannot be scoped.
// Nothing here can grant access; it can only avoid showing a door that will not open.
//
// ── THE ROLE MODEL ───────────────────────────────────────────────────────────
// Template §8 proposes `role × scope` with roles `employee < admin < masteradmin`. This ERP's roles
// are FROZEN in `functions/src/constants/roles.js` as exactly four designations, and the second axis
// is `department`, not an open scope set. So the shape of §8 is followed — named predicates in one
// file, called from nav, routes and actions — with this project's frozen vocabulary substituted for
// the template's example one. Inventing a `masteradmin` role to match the document would put the
// client and the frozen backend into permanent disagreement.

export const DESIGNATIONS = {
  MASTER_ADMIN: "Master Admin",
  DEPARTMENT_ADMIN: "Department Admin",
  EMPLOYEE: "Employee",
  LAWYER: "Lawyer",
};

export const USER_TYPES = { STAFF: "staff", CUSTOMER: "customer" };

// ── Role predicates ─────────────────────────────────────────────────────────
export const isMasterAdmin = (user) => user?.designation === DESIGNATIONS.MASTER_ADMIN;

export const isDepartmentAdmin = (user) => user?.designation === DESIGNATIONS.DEPARTMENT_ADMIN;

export const isAdmin = (user) => isMasterAdmin(user) || isDepartmentAdmin(user);

export const isStaff = (user) =>
  !!user && user.userType !== USER_TYPES.CUSTOMER && Object.values(DESIGNATIONS).includes(user.designation);

// ── Capability predicates ───────────────────────────────────────────────────
// Named after the CAPABILITY, not the role (template §8). When "department admins may also schedule
// reports" becomes true, one predicate changes and every route, nav item and button is instantly
// correct — instead of a 40-file audit of `user.designation === "Master Admin"`.

/** The reports area at all. Customers are refused by the router; this hides it from them too. */
export const canViewReports = (user) => isStaff(user);

/** The Custom Report Builder and saved templates. Any staff caller may build over their own scope. */
export const canBuildReports = (user) => isStaff(user);

/**
 * Report schedules. Master Admin only — `reportRoutes.js` gates every `/reports/schedules*` route
 * with `masterOnlyGate`, so showing it to anyone else would render a page that 403s.
 */
export const canManageSchedules = (user) => isMasterAdmin(user);

/** The unrestricted export history. Others see only their own runs, so the page is still useful. */
export const canViewAllExports = (user) => isMasterAdmin(user);

/** §O audit and compliance reports. `roles: MASTER_ONLY` on every one of those catalogue rows. */
export const canViewAuditReports = (user) => isMasterAdmin(user);

/**
 * Whether this caller may run a given catalogue row.
 *
 * The backend already filters `GET /reports/catalogue` to what the caller may run, so this is a
 * second, local check for rows reached by a bookmarked URL. It reads the row's own `availability`
 * too: an unavailable report is listed, but running it returns 422 with the frozen finding that
 * explains why — so the UI offers "why is this unavailable?" instead of a Run button.
 */
export const canRunReport = (user, row) => isStaff(user) && row?.availability === "Available";

// ── Asset URL helper (lives here because it needs no other module) ───────────
export function assetUrl(key) {
  if (!key) return null;
  const base = import.meta.env.VITE_ASSET_URL;
  return base ? `${base}/${key}` : key;
}

// ─────────────────────────────────────────────────────────────────────────────
// Module 1 — Users & Employee Management
// ─────────────────────────────────────────────────────────────────────────────
// Each predicate mirrors ONE guard chain in `functions/src/routers/userRoutes.js`. All of it is
// cosmetic: the router re-checks the designation and `userService` re-checks the department scope and
// the no-escalation rule on every request. These exist so the panel does not offer a door that will
// not open.

export const DEPARTMENTS = {
  ADMINISTRATION: "Administration",
  PROPERTIES: "Properties",
  FIELD_OPERATIONS: "Field Operations",
  LEGAL: "Legal",
  REGISTRATION_SALES: "Registration cum Sales",
  CUSTOMER_SUPPORT: "Customer Support",
};

/**
 * THE OPERATING DEPARTMENTS — every department except Administration.
 *
 * Four frozen backend modules resolve a read tier from the caller's department and end the same way:
 *
 *   customerService.resolveReadTier        T1 Sales/Support · T2 Properties/Field Ops/Legal · else 403
 *   bookingService.resolveBookingReadTier  FULL Sales/Support · RESTRICTED Properties/Field Ops/Legal · else 403
 *   reallocationService.resolveReadTier    FULL Sales · SUPPORT Support · RESTRICTED Properties/Field Ops/Legal · else 403
 *   serviceRequestService.resolveReadTier  FULL Support · DEPARTMENT the other four · else 403
 *
 * The tiers differ; the ADMITTED SET does not. Each grants some tier to all five operating
 * departments and refuses Administration staff who are not the Master Admin — the deny-by-default
 * posture frozen Modules 7, 8 and 9 take. One constant therefore expresses all four, and the
 * predicates below read from it rather than each restating a department list of their own.
 *
 * ⚠ THIS IS NOT A SECOND AUTHORIZATION MODEL. It mirrors a decision the backend has already made and
 * is still the real gate; nothing here grants anything. Its only job is to stop the UI offering a
 * door the API will refuse to open.
 */
export const OPERATING_DEPARTMENTS = [
  DEPARTMENTS.CUSTOMER_SUPPORT,
  DEPARTMENTS.REGISTRATION_SALES,
  DEPARTMENTS.LEGAL,
  DEPARTMENTS.PROPERTIES,
  DEPARTMENTS.FIELD_OPERATIONS,
];

/** Master Admin, or staff of an operating department — the shape all four tier resolvers share. */
export const hasOperatingDepartment = (user) =>
  isMasterAdmin(user) || (isStaff(user) && OPERATING_DEPARTMENTS.includes(user?.department));

/** GET /users — Master Admin or Department Admin. */
export const canViewUsers = (user) => isAdmin(user);

/**
 * POST /users/staff · PUT /users/staff/:id · PATCH .../status — same gate.
 * A Department Admin manages only their own department's staff, and Administration has no employees (owner
 * department-structure ruling, 2026-10-14) — so the Administration Department Admin is offered no add/edit/status
 * controls: there is nobody they could manage. The server refuses any Administration employee as well.
 */
export const canManageStaff = (user) =>
  isMasterAdmin(user) || (isDepartmentAdmin(user) && OPERATING_DEPARTMENTS.includes(user?.department));

/**
 * POST /users/staff/:id/reset-password — `requireDesignation(DEPARTMENT_ADMIN)` composed with
 * `requireDepartment(ADMINISTRATION)`. Module 1's own router comment states the composition means
 * "Administration Department Admin or Master Admin (override)", so the Master Admin passes.
 */
export const canResetPassword = (user) =>
  isMasterAdmin(user) ||
  (isDepartmentAdmin(user) && user?.department === DEPARTMENTS.ADMINISTRATION);

/** POST|PUT /users/customers — `requireDepartment(REGISTRATION_SALES)`; Master Admin overrides. */
export const canManageCustomerLogins = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.REGISTRATION_SALES;

/**
 * Whether this caller may assign a given designation.
 *
 * `userService.assertCanProvisionStaff`: a Department Admin is confined to their own department and
 * may NOT assign Master Admin or Department Admin. Mirrored so the form greys those options out
 * instead of letting the user fill a whole form and collect a 403 on submit.
 */
export const canAssignDesignation = (user, designation) => {
  if (isMasterAdmin(user)) return true;
  if (!isDepartmentAdmin(user)) return false;
  return designation !== "Master Admin" && designation !== "Department Admin";
};

/** A Department Admin may only provision into their own department. */
export const canAssignDepartment = (user, department) =>
  isMasterAdmin(user) || (isDepartmentAdmin(user) && user?.department === department);

/**
 * The activity summary reads Module 13's audit search, which is Master/Department-Admin gated.
 * Consumed read-only through Module 13's published operation — no Module 13 file is touched.
 */
export const canViewUserActivity = (user) => isAdmin(user);

// ─────────────────────────────────────────────────────────────────────────────
// Module 2 — Property Management
// ─────────────────────────────────────────────────────────────────────────────
// Reads are open to any active staff member (`propertyRoutes` GET routes carry no department gate).
// Writes are `requireDepartment(Properties)`; a Master Admin passes by the same override that
// applies everywhere else in this codebase.

/** POST /properties · PUT /properties/:id · PATCH /properties/:id/submit-verification. */
/**
 * Whether this caller may submit a property for FIELD VERIFICATION.
 *
 * STRICTLY NARROWER THAN `canWriteProperty`, and separate from it on purpose. Editing a property is
 * ordinary Properties Department work; handing the asset to Field Operations ends Module 2's
 * ownership of the record and starts another department's workload, so it is an ADMINISTRATIVE act.
 * A Properties Department EMPLOYEE keeps every editing right and does not get this one.
 *
 * Mirrors `propertyService.assertCanSubmitPropertyForVerification`, which is the authority; this
 * exists so the control is not rendered where it would 403.
 */
export const canSubmitPropertyForVerification = (user) =>
  isMasterAdmin(user) || (isDepartmentAdmin(user) && user?.department === DEPARTMENTS.PROPERTIES);

export const canWriteProperty = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.PROPERTIES;

/**
 * POST /properties — REGISTERING a property (PROP-01, owner ruling 2026-09-05).
 *
 * STRICTLY NARROWER THAN `canWriteProperty`, and separate from it on purpose — the same relationship
 * `canSubmitPropertyForVerification` above has, drawn at the other end of the workflow. Registration
 * STARTS the asset's life and ASSIGNS THE WORK: the registrar names the Data Completion employee,
 * enters the deadline and is recorded as the task's assignor. That is task administration, and an
 * Employee or Lawyer never administers a department (13T AUTH-04/BR-06).
 *
 * EDITING IS UNTOUCHED. `canWriteProperty` still admits the whole department, because the assigned
 * employee's Data Completion work IS an edit — narrowing it would deny them the very work the ruling
 * gives them.
 *
 * Mirrors `propertyService.assertCanRegisterProperty`, which is the authority; this exists so the
 * control is not rendered where it would 403, and the route guard is not the enforcement.
 */
/**
 * Whether this caller may open the FULL dynamic Data Completion form for a property (PROP-03 §6/§14).
 *
 * ── IT IS NOT `canWriteProperty`, AND THAT IS THE POINT ────────────────────
 * `canWriteProperty` answers "may this actor call PUT /properties/:id at all", and stays department-
 * wide because the assigned employee's completion work IS that call. This answers the narrower
 * question the Edit button actually asks: "will the completion form this opens be usable by them?"
 *
 * Once a Data Completion task exists, `propertyService.updateProperty` refuses an administering actor
 * every completion-owned field, so for a Department Admin the answer becomes no — and the button is
 * withdrawn rather than leading them into a form whose Save cannot succeed. The Master Admin is
 * exempt, preserving existing authority, and an Employee is unaffected: their access comes from the
 * task, and 13X already decides it.
 *
 * `recovery.everAssigned` is the server's own answer to "has this property EVER had a Data Completion
 * task, in any status" — which is precisely the condition the server's lock keys on.
 *
 * ⚠ IT IS NOT `recovery.available`, AND THE DIFFERENCE IS NOT COSMETIC. An earlier version used
 * availability as the proxy and browser testing caught it: a Draft whose task is COMPLETED is
 * recoverable (mode `promote`) AND already handed over, so the button reappeared on exactly the
 * property whose completion fields the server refuses. A cancelled task is the same trap — the work
 * was rolled back, but the hand-off still happened. Only "was a task ever created" matches the lock.
 */
export const canCompletePropertyData = (user, recovery) => {
  if (isMasterAdmin(user)) return true;
  if (!canWriteProperty(user)) return false;
  // A Department Admin keeps it only while the property has never been handed over.
  if (isDepartmentAdmin(user)) return !recovery?.everAssigned;
  return true;
};

export const canRegisterProperty = (user) =>
  isMasterAdmin(user) || (isDepartmentAdmin(user) && user?.department === DEPARTMENTS.PROPERTIES);

/**
 * Whether this caller may change a property's PROPERTY TYPE (PROP-03 §22/§23).
 *
 * ── THE ONE FIELD THAT DECIDES SOMEBODY ELSE'S FORM ────────────────────────
 * `assetType` is the input Module 15F resolves the completion form from, so changing it re-points the
 * assigned employee's own form at a different field set. §1 makes that selection the Admin's — "PROPERTY
 * ADMIN ... selects Property Type" — and §22 draws the consequence: the employee must not be able to
 * switch it to obtain another form.
 *
 * THE SAME RULE AS REGISTRATION, SO THE SAME PREDICATE. The actor who chooses the type when the asset is
 * created is the actor who may still correct it. Delegating rather than restating is what stops the two
 * drifting apart — and it keeps §23's "preserve legitimate Property Type changes" true by construction.
 *
 * ADVISORY ONLY. `propertyService.assertMayChangePropertyType` refuses the write whatever this returns;
 * this exists so the employee's completion form shows the type as a fact rather than offering a control
 * whose Save cannot succeed.
 */
export const canChangePropertyType = (user) => canRegisterProperty(user);

/** GET /properties and GET /properties/:id — any active staff account. */
export const canViewProperties = (user) => isStaff(user);

/**
 * Whether this caller reads the property register UNRESTRICTED, or is scoped to their own tasks.
 *
 * ── THIS IS NOT A PERMISSION. IT IS A REACHABILITY QUESTION ─────────────────
 * `canViewProperties` above answers "may this actor call the endpoint" and is the gate. This answers
 * a different and narrower question that only one caller needs: "will the property this actor is
 * about to create be readable BY THEM the moment it exists?" Nothing is granted or withheld here.
 *
 * ── MIRRORS 13X, WHICH IS THE AUTHORITY ────────────────────────────────────
 * `entityAccessService` resolves an asset read in three steps, and only the first two are
 * unconditional: Step 1 admits the Master Admin organisation-wide, and Step 2's Ruling C branch
 * admits the PROPERTIES Department Admin unrestricted because "Properties OWNS the asset domain, and
 * its register -> read -> edit loop creates records that no task yet names". Step 3 resolves an
 * Employee or a Lawyer FROM THEIR OWN ASSIGNED TASKS, every time, and `createdBy` is deliberately
 * NOT part of that grant.
 *
 * So a Properties EMPLOYEE reads a property only once a task names it, and `createdBy` is never the
 * reason. Under PROP-01 (owner ruling, 2026-09-05) that task is created BY THE REGISTRATION ITSELF —
 * the Admin registers the property and assigns its Data Completion task in one act — so the assigned
 * employee's access exists from the moment the property does.
 *
 * ── IT IS NO LONGER A NAVIGATION DECISION ──────────────────────────────────
 * Its former consumer was `AddProperty`, which had to decide where to send a registrar who could not
 * read what they had just made. That case cannot arise now: only an actor with unrestricted scope may
 * register at all (`canRegisterProperty`). The predicate is kept because it states 13X's read matrix
 * correctly and is asserted as a contract, not because a screen still branches on it.
 */
export const hasUnrestrictedPropertyScope = (user) =>
  isMasterAdmin(user) || (isDepartmentAdmin(user) && user?.department === DEPARTMENTS.PROPERTIES);

// ─────────────────────────────────────────────────────────────────────────────
// Module 3 — Venture Management
// ─────────────────────────────────────────────────────────────────────────────
// Identical gate shape to Module 2: reads open to all four staff designations (staffReadGate),
// writes confined to the Properties department with the usual Master Admin override.

/** POST /ventures · PUT /ventures/:id. */
export const canWriteVenture = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.PROPERTIES;

/** GET /ventures and GET /ventures/:id — staffReadGate. */
export const canViewVentures = (user) => isStaff(user);

// ─────────────────────────────────────────────────────────────────────────────
// Module 4 — Plot Management
// ─────────────────────────────────────────────────────────────────────────────
// Same gate shape as Modules 2 and 3: reads open to all four staff designations, writes confined to
// the Properties department with the Master Admin override.

/** POST /plots · /plots/bulk · /plots/merge · PUT /plots/:id · POST /plots/:id/split. */
export const canWritePlot = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.PROPERTIES;

/** GET /plots and GET /plots/:id — staffReadGate. */
export const canViewPlots = (user) => isStaff(user);

// ─────────────────────────────────────────────────────────────────────────────
// Customer Support Phase 2 — Historical Sales
// ─────────────────────────────────────────────────────────────────────────────
// MIRRORS THE SERVER EXACTLY. Every `/historical-sales` route is gated to the Properties department with
// the Master Admin override, and the service refuses everyone else. Customer Support has no general
// authority over historical sales — it can match or verify one only from inside its own application
// workflow, which is not a request the browser makes. These helpers only decide what to render.

/** Every `/historical-sales` read and write. */
export const canManageHistoricalSales = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.PROPERTIES;

/** Part 4 (BD-P4-11): recording, editing and bulk-marking are the Properties Department's work; the Master Admin reads. */
export const canOperateHistoricalSales = (user) => !isMasterAdmin(user) && user?.department === DEPARTMENTS.PROPERTIES;

/** POST /historical-sales/:id/reject — the Properties Department ADMIN, or the Master Admin. */
export const canRejectHistoricalSale = (user) =>
  isMasterAdmin(user) || (user?.department === DEPARTMENTS.PROPERTIES && isDepartmentAdmin(user));

// ─────────────────────────────────────────────────────────────────────────────
// Customer Support Phase 6 — the onboarding workspace (historical customer applications)
// ─────────────────────────────────────────────────────────────────────────────
// MIRRORS THE SERVER. `/historical-applications` is gated to Customer Support with the Master Admin override; the
// service then lets the Master Admin READ and nothing else (owner ruling R10). Which act a Customer Support member
// may take on one application is the application's own `allowed`, sent by the server — never guessed here.

/** GET /historical-applications and /:id — Customer Support, and the Master Admin read-only. */
export const canViewHistoricalApplications = (user) =>
  isMasterAdmin(user) || (isStaff(user) && user?.department === DEPARTMENTS.CUSTOMER_SUPPORT);

/**
 * Customer Support Phase 7 (P7-R18, P7-R19, CL-U1.4) — GET /historical-customers: the Customer Support ADMIN and the
 * Master Admin (read-only). A Customer Support Employee is refused by the server too, so this only hides what it refuses.
 */
export const canViewHistoricalCustomers = (user) =>
  isMasterAdmin(user) || (isStaff(user) && user?.department === DEPARTMENTS.CUSTOMER_SUPPORT && isDepartmentAdmin(user));

/** The applications card on the Dashboard (R9) — Customer Support's own work, so not the Master Admin. */
export const isCustomerSupportStaff = (user) =>
  !isMasterAdmin(user) && isStaff(user) && user?.department === DEPARTMENTS.CUSTOMER_SUPPORT;

/**
 * Legal Part 5 §13 — the Property department's document-request queue.
 *
 * MIRRORS THE SERVER EXACTLY. `legalDocumentRequestService.assertCanFulfil` admits the Properties
 * department and the Master Admin, and nobody else — not Legal, who raise the requests but do not
 * answer them. This helper only decides whether to render the link; the server decides the rest.
 */
export const canFulfilDocumentRequests = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.PROPERTIES;

/**
 * DELETE /plots/:id — REMOVE FROM INVENTORY (Property Part 1 §41).
 *
 * THE RULE THAT WAS ALWAYS EXPECTED TO DIVERGE HAS NOW DIVERGED. This predicate used to be an alias
 * of `canWritePlot`, with a note saying it was named separately in case the rule ever differed. §41
 * makes it differ: removing a unit from inventory is an ADMINISTRATIVE act, not ordinary unit
 * editing, so an ordinary Properties employee — who may still create, edit, split and merge units —
 * may no longer perform it.
 *
 * Mirrors `plotService.assertCanAdministerUnit` exactly. Cosmetic, as always: the server refuses the
 * request regardless of what this returns.
 */
export const canDeletePlot = (user) =>
  isMasterAdmin(user) ||
  (user?.department === DEPARTMENTS.PROPERTIES &&
    user?.designation === DESIGNATIONS.DEPARTMENT_ADMIN);

// ─────────────────────────────────────────────────────────────────────────────
// Module 5 — Field Verification
// ─────────────────────────────────────────────────────────────────────────────
// Reads are staffReadGate. EVERY write is requireDepartment(Field Operations), and three of them are
// additionally confined to the ASSIGNED WORKER by `assertAssignedWorker` — a rule the route cannot
// apply because it depends on the record. Mirrored here so a worker is not offered another worker`s
// actions, and re-enforced server-side regardless.

/** Any Field Operations write route. Master Admin overrides, as everywhere else. */
export const canWriteVerification = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.FIELD_OPERATIONS;

/** GET /verifications and GET /verifications/:id — staffReadGate. */
export const canViewVerifications = (user) => isStaff(user);

/**
 * `GET /verifications/queue` — the Field Verification DEPARTMENT QUEUE.
 *
 * A DEPARTMENT-ADMIN SURFACE, mirroring 13T's `canAdministerTasks`: the Master Admin, or the
 * Department Admin **of Field Operations**. Nobody else — not a Field Verifier, not a Department
 * Admin of another department.
 *
 * WHY AN EMPLOYEE IS EXCLUDED: the queue exists to ASSIGN work, and only these two can assign
 * (`assertCanAssign`). A Field Verifier would see nothing but disabled controls; their surface is
 * `/verifications/assigned`. This mirrors 13T, whose department queue "is not reachable by an
 * employee at all".
 *
 * This predicate is deliberately NARROWER than `canWriteVerification` (which admits any Field
 * Operations member, because accept/record/submit are a worker's actions). The server enforces the
 * same rule — this only keeps the UI from offering a door that leads to a 403.
 */
export const canAdministerVerificationQueue = (user) =>
  isMasterAdmin(user) ||
  (isDepartmentAdmin(user) && user?.department === DEPARTMENTS.FIELD_OPERATIONS);

/**
 * The Field → Legal HANDOFF — Part 3 §4/§26/§37.
 *
 * THE SAME SET AS `canAdministerVerificationQueue`, and deliberately so: the person who assigns field
 * work and reviews its result is the person who hands it to Legal. Mirrors the server's
 * `legalIntakeService.assertCanSubmitToLegal`, which is the real gate.
 *
 * A FIELD EMPLOYEE NEVER PASSES. That is Part 3's central ownership rule, and it is enforced on the
 * server — this predicate only keeps the door from being drawn where it would 403.
 *
 * IT GRANTS NOTHING IN THE LEGAL MODULE. Submitting a handoff is not creating a legal case: a Field
 * Admin still cannot open, edit, assign or decide one (`canWriteLegal` / `canReviewLegal` are
 * unchanged, and the backend's `assertCanCreateCase` still admits Legal and the Master Admin only).
 */
//
// FIELD STEP 2 FINAL §12/§113 (owner, 2026-09-12) — the Field Operations Department Admin ONLY. The
// Master Admin is not a Field operational handoff actor; they keep the oversight READ of the board
// through `canViewLegalHandoff` below. Mirrors the server's `assertCanSubmitToLegal`.
export const canSubmitUnitsToLegal = (user) =>
  isDepartmentAdmin(user) && user?.department === DEPARTMENTS.FIELD_OPERATIONS;

/**
 * FIELD STEP 2 FINAL §12 — who may OPEN the Legal Handoff board: the Field Operations Department
 * Admin, who acts on it, and the Master Admin, whose read is oversight only (no submit control is
 * drawn for them). Mirrors the server's `assertCanViewHandoffBoard`.
 */
export const canViewLegalHandoff = (user) => canAdministerVerificationQueue(user);

/**
 * The LEGAL intake queue — Part 3 §25/§26.
 *
 * The Master Admin, or the Legal Department Admin. Mirrors the server's
 * `assertCanReceiveLegalIntake`, and matches the set Module 6 already admits to its own review
 * surfaces. Field Operations cannot read this queue: the handoff is one-way, and downstream
 * visibility for the Field Admin is read-only and lives on their own board.
 */
export const canReceiveLegalIntake = (user) =>
  isMasterAdmin(user) || (isDepartmentAdmin(user) && user?.department === DEPARTMENTS.LEGAL);

/**
 * LEGAL PHASE 1 (2026-09-12) · S4 — who may ACT on incoming Legal work: assign units, assign parent
 * assessment tasks. The Legal Department Admin only. `canReceiveLegalIntake` above stays the READ
 * gate (the Master Admin keeps oversight of the queue); this one decides which controls are drawn,
 * and mirrors the server's `assertCanActOnLegalIntake` / `assertCanAssess(null, null)` exactly.
 */
export const canActOnLegalIntake = (user) =>
  isDepartmentAdmin(user) && user?.department === DEPARTMENTS.LEGAL;

/**
 * Legal Part 9 — the Legal Department reporting board.
 *
 * THE SAME SET AS `canReceiveLegalIntake`, and deliberately not a wider one. Part 9 §23 is explicit
 * that a reporting endpoint existing is not a reason to broaden access: the board enumerates every
 * lawyer's work, every open issue and every unit's saleability, which is the Department Admin's view
 * of their own department and nobody else's.
 *
 * Written as its own predicate rather than aliased, so that if the reporting tier ever legitimately
 * diverges from the intake tier, the change happens in one obvious place instead of silently
 * widening the queue authority along with it. It mirrors the server's
 * `legalReportingService.assertCanViewLegalReporting`, which re-checks independently.
 */
export const canViewLegalReporting = (user) =>
  isMasterAdmin(user) || (isDepartmentAdmin(user) && user?.department === DEPARTMENTS.LEGAL);

/**
 * Legal Part 11 — the NOC review board.
 *
 * THE SAME SET AS THE INTAKE AND REPORTING TIERS, and deliberately not a wider one: accepting a NOC
 * is what releases a unit, and §23 gives that decision to the Legal Department Admin alone. A LAWYER
 * IS DELIBERATELY EXCLUDED even though they own the NOC — the two roles must stay disjoint so that
 * "approve your own NOC" is not merely refused but unreachable.
 *
 * Written as its own predicate rather than aliased, so that if the review tier ever legitimately
 * diverges the change happens in one obvious place. It mirrors the server's
 * `legalNocService.assertCanReview`, which re-checks independently.
 */
export const canReviewNoc = (user) =>
  isMasterAdmin(user) || (isDepartmentAdmin(user) && user?.department === DEPARTMENTS.LEGAL);

/**
 * D-12 (owner ruling B, 2026-09-04) — the Legal Admin's additional-document FORWARDING queue.
 *
 * MIRRORS THE SERVER'S `assertCanForward` EXACTLY, and mirrors it because the queue's whole purpose
 * is the forward decision: a reader of this queue is precisely someone who could act on every row in
 * it. `GET /legal-document-requests/awaiting-forward` re-checks this server-side and is the only
 * thing actually enforcing it — this predicate exists so a Lawyer is not offered a row that 403s.
 *
 * THE SAME SET AS THE INTAKE, REPORTING AND NOC TIERS, and written as its own predicate rather than
 * aliased to one of them — the convention those three already established here, so that if the
 * forwarding tier ever legitimately diverges the change happens in one obvious place instead of
 * silently moving three unrelated capabilities with it.
 *
 * NOT the Legal EMPLOYEE and NOT the Lawyer: forwarding another department's work is the Department
 * Admin's decision (business/07 §3, Amendment A2). C3 — whether the Lawyer takes a distinct sidebar
 * at all — is a SEPARATE unresolved decision and is untouched by this.
 */
// LEGAL PHASE 1 (2026-09-12) · S4 — mirrors the narrowed server assertion: the Legal Department Admin
// only. The Master Admin is no longer a forwarding authority, so they are no longer offered the queue.
export const canForwardLegalDocumentRequest = (user) =>
  isDepartmentAdmin(user) && user?.department === DEPARTMENTS.LEGAL;

/**
 * accept / record evidence / submit — `assertAssignedWorker`: THE ASSIGNEE, AND NOBODY ELSE.
 * Being in Field Operations is NOT sufficient; another department member sees the record read-only.
 *
 * NO DESIGNATION PASSES HERE, THE MASTER ADMIN INCLUDED. This mirrors the server exactly, which is
 * the only reason it is safe to render anything off it: `assertAssignedWorker` withdrew the
 * Master Admin's on-behalf override, so a UI that still offered them the evidence form would be
 * offering work the server refuses — a button that exists only to fail. Reading, assigning,
 * reassigning and reviewing are unaffected and keep their own predicates below.
 */
export const isAssignedWorker = (user, verification) => {
  if (!user || !verification) return false;
  return String(verification.assignedTo || "") === String(user._id);
};

export const canActAsWorker = (user, verification) =>
  canWriteVerification(user) && isAssignedWorker(user, verification);

/**
 * reassign and review carry NO assignee restriction — any Field Operations member may perform them.
 * Kept as its own predicate so the distinction from `canActAsWorker` stays explicit.
 */
// FIELD PHASE 3 §7/§45 — REVIEW (and reassignment) IS THE ADMINISTRATOR'S. It was `canWriteVerification`, so every Field operative was shown Approve / Return / Revisit on their own records, which the server (`assertCanAssign`: Master or the Field Operations Department Admin) refuses. The UI now offers exactly what the server will perform.
//
// FINAL CORRECTION (owner, 2026-09-12) §13/§38 — the Field Operations Department Admin ONLY. The
// Master Admin keeps read-only oversight (`canAdministerVerificationQueue` still opens the Field
// Queue, the records and the review page) but is drawn no Approve / Return / Revisit control.
// Mirrors the server's `assertCanAssign`.
export const canReviewVerification = (user) =>
  isDepartmentAdmin(user) && user?.department === DEPARTMENTS.FIELD_OPERATIONS;

/**
 * FINAL CORRECTION §13/§14 — who may ACT on Field work: assign, split, reassign, review, hand off.
 * The Field Operations Department Admin only; the Master Admin's views are read-only.
 */
export const canOperateFieldWork = (user) => canReviewVerification(user);

/**
 * FINAL CORRECTION §16/§21 — who may OPEN a Field Verification record at all: the Master Admin
 * (oversight), Field Operations, and Legal (the server admits Legal to handed-over records only).
 * Every other department sees a Field scope's state on its own pages, never the record.
 */
export const canReadFieldDetail = (user) => isMasterAdmin(user)
  || [DEPARTMENTS.FIELD_OPERATIONS, DEPARTMENTS.LEGAL].includes(user?.department);

// ─────────────────────────────────────────────────────────────────────────────
// Module 6 — Legal Workflow
// ─────────────────────────────────────────────────────────────────────────────
// Three gates, and one service-level rule the routes cannot express.

/** Every /legal write except reopen and the approval rules — requireDepartment(Legal). */
export const canWriteLegal = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.LEGAL;

/** staffReadGate — all four designations. */
export const canViewLegal = (user) => isStaff(user);

/**
 * The LEGAL REVIEW workspace — Legal Part 3 §10.
 *
 * Any LEGAL member, plus the Master Admin. Mirrors the route's `requireDepartment(LEGAL)` gate — the
 * COARSE half only, and deliberately so: which Legal member may read or write a PARTICULAR review is
 * `legalReviewService`'s decision (the assignee writes; a colleague gets a 404), and it is enforced
 * on the server where it cannot be bypassed. This predicate only keeps the door from being drawn for
 * departments that could never open it.
 */
export const canOpenLegalReview = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.LEGAL;

/** A Lawyer — the Legal department's operational designation. Not every Legal member is one. */
export const isLawyer = (user) => user?.designation === DESIGNATIONS.LAWYER;

/**
 * LEGAL PHASE 2 §9/§10 — the Legal WORK and RECORD doors (Document Requests, Legal Reviews, Legal
 * Issues): the Legal Department Admin and the Lawyer, plus the Master Admin's oversight read.
 *
 * A LEGAL EMPLOYEE IS NOT A LAWYER (§11) and is not offered these rows. Mirrors the list endpoints'
 * department gate only; WHICH records come back is the server's decision (department-wide for the
 * Legal Admin and the Master Admin, the caller's own tasks for everyone else).
 */
export const canUseLegalWork = (user) =>
  isMasterAdmin(user)
  || (user?.department === DEPARTMENTS.LEGAL && (isDepartmentAdmin(user) || isLawyer(user)));

/**
 * Review and workflow decisions: "Only the Legal Department Admin or Master Admin may review a legal
 * case". The ROUTE only checks the Legal department, so a Lawyer passes the route and is refused by
 * the service. Mirrored here so a Lawyer is not offered a button that always 403s.
 */
export const canReviewLegal = (user) =>
  isMasterAdmin(user) || (isDepartmentAdmin(user) && user?.department === DEPARTMENTS.LEGAL);

/** Reopen is masterAdminGate — the only designation-gated case operation in Module 6. */
export const canReopenLegal = (user) => isMasterAdmin(user);

/** Approval rules are masterAdminGate throughout. */
export const canManageApprovalRules = (user) => isMasterAdmin(user);

/** Issue master is legalWriteGate for writes, staffReadGate for reads. */
export const canManageIssueMaster = (user) => canWriteLegal(user);

// ─────────────────────────────────────────────────────────────────────────────
// Module 7 — Customer Management
// ─────────────────────────────────────────────────────────────────────────────

/** Every /customers write — requireDepartment(Registration cum Sales). */
export const canWriteCustomer = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.REGISTRATION_SALES;

/**
 * staffReadGate on the list; selfReadGate on the detail — and then `customerService.resolveReadTier`
 * grants T1 to Sales/Customer Support, T2 to Properties/Field Operations/Legal, and REFUSES everyone
 * else with 403 (MDS 07 §10, deny-by-default).
 *
 * WAS `isStaff(user)`, which offered the module to Administration staff whose every request the API
 * then refused — a door that will not open. The T2 departments keep their limited read exactly as the
 * frozen rule grants it: they have a legitimate need to know WHO owns an asset, and this predicate
 * does not second-guess that.
 */
export const canViewCustomers = (user) => hasOperatingDepartment(user);

/**
 * The unmasked identity read is customerWriteGate — the same department gate, but named separately
 * because it is the ONE call that reveals protected identity numbers and is audited as such. A
 * distinct predicate means that if the rule is ever tightened, exactly one place changes.
 */
export const canViewUnmaskedIdentity = (user) => canWriteCustomer(user);

/** Blacklisting, and reversing it, is Master-Admin-only — enforced in customerService. */
export const canBlacklist = (user) => isMasterAdmin(user);

/** Linking a portal login to a customer record. */
export const canManageCustomerLogin = (user) => canWriteCustomer(user);

// ─────────────────────────────────────────────────────────────────────────────
// Module 8 — Sales & Allocation
// ─────────────────────────────────────────────────────────────────────────────

/** Every booking and payment WRITE — requireDepartment(Registration cum Sales). */
export const canWriteSales = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.REGISTRATION_SALES;

/**
 * staffReadGate on lists; selfReadGate on detail — then `bookingService.resolveBookingReadTier` grants
 * FULL to Sales/Customer Support, RESTRICTED to Properties/Field Operations/Legal, and 403s the rest.
 * WAS `isStaff(user)`; Administration staff were shown a module the API refuses.
 */
export const canViewSales = (user) => hasOperatingDepartment(user);

// ─────────────────────────────────────────────────────────────────────────────
// Registration & Sales Part 1 — sales inventory, activation and assignment
// ─────────────────────────────────────────────────────────────────────────────
// Each predicate mirrors ONE guard in `functions/src/routers/salesInventoryRoutes.js` plus the
// designation rule its service applies. THE SERVER IS THE AUTHORITY: Part 1 §23 is explicit that
// hiding a control is not authorization, so these decide only what is OFFERED. Every endpoint
// re-checks, and the backend tests exercise the refusals with no UI involved at all.

/**
 * The sales inventory AREA — `requireDepartment(Registration cum Sales)` on every route, which the
 * Master Admin passes by the built-in override. A Sales EMPLOYEE is admitted here deliberately: the
 * service scopes them to their own assigned work rather than refusing them the area outright.
 */
export const canViewSalesInventory = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.REGISTRATION_SALES;

/**
 * The two writes — activate and assign — and the admin-only boards (employees, history, the
 * assignment selector). `salesInventoryService.assertSalesAdmin`: the Registration cum Sales
 * Department Admin, or the Master Admin. A Sales Employee is refused with 403 at the API.
 */
export const canAdministerSalesInventory = (user) =>
  isMasterAdmin(user) ||
  (isDepartmentAdmin(user) && user?.department === DEPARTMENTS.REGISTRATION_SALES);

/**
 * Registration & Sales Part 2 — the Sales EMPLOYEE workspace.
 *
 * Mirrors `requireDepartment(Registration cum Sales)` on `salesEmployeeRoutes.js`. The Master Admin
 * passes by the built-in override, and a Department Admin is admitted too so they can see what an
 * employee sees — with the SAME own-work scoping, because the surface answers "what is assigned to
 * ME". WHICH task, plot or customer any of them may reach is a per-record rule the server decides;
 * no client predicate can express it, and this one does not try (§3/§47).
 */
export const canUseSalesWorkspace = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.REGISTRATION_SALES;

/**
 * Payment verification is a DESIGNATION gate (Master Admin or Department Admin) — deliberately
 * different from the department gate on recording. `paymentService` additionally forbids the person
 * who RECORDED a payment from verifying it, which no client-side check can pre-empt reliably: the
 * recorder id is on the record, so the UI checks it where it can and the server is the authority.
 */
export const canVerifyPayment = (user) => isAdmin(user);

/** Whether this caller may verify THIS payment — recorder ≠ verifier. */
export const canVerifyThisPayment = (user, payment) => {
  if (!canVerifyPayment(user) || !payment) return false;
  return String(payment.collectedBy || "") !== String(user._id);
};

/** Registration-gate override and the sales configuration are masterOnlyGate. */
export const canOverrideRegistrationGate = (user) => isMasterAdmin(user);
export const canManageSalesConfig = (user) => isMasterAdmin(user);

// ─────────────────────────────────────────────────────────────────────────────
// Module 9 — Reallocation
// ─────────────────────────────────────────────────────────────────────────────
// Module 9 has the WIDEST gate spread in the project: six distinct department sets across nineteen
// routes, because a reallocation is raised by whoever notices the problem and executed only by the
// one department that owns the booking. Each predicate below names exactly one of them.

/**
 * Raise, submit for approval, and cancel — `requireDepartment` over FIVE departments. This is the
 * widest write gate in the codebase and it is deliberate: business/10 §2's reason categories span
 * legal, engineering, financial and customer causes, so confining the raise to one department would
 * force every other department to route requests through it.
 */
export const REALLOCATION_RAISE_DEPARTMENTS = [
  DEPARTMENTS.REGISTRATION_SALES,
  DEPARTMENTS.CUSTOMER_SUPPORT,
  DEPARTMENTS.LEGAL,
  DEPARTMENTS.PROPERTIES,
  DEPARTMENTS.FIELD_OPERATIONS,
];

export const canRaiseReallocation = (user) =>
  isMasterAdmin(user) || REALLOCATION_RAISE_DEPARTMENTS.includes(user?.department);

/**
 * verify-reason carries the same five-department ROUTE gate as raising — but the route is not the
 * real rule.
 *
 * `reallocationService.VERIFYING_DEPARTMENTS` narrows verification to the department that OWNS the
 * reason category, and the frozen source says why: "the department that owns the reason verifies it.
 * This is a value-dependent rule a route guard cannot express, which is why it lives here."
 * Categories H (Management Decisions) and I (Force Majeure) map to an EMPTY list — Master Admin only.
 *
 * So this predicate takes the category. Called without one it answers the route-level question,
 * which is the right answer for "should this person see the reallocations area at all" and the wrong
 * answer for "should the Verify button be enabled" — the caller supplies the category for that.
 */
const VERIFYING_DEPARTMENTS_BY_CATEGORY = {
  "A Customer Preference": [DEPARTMENTS.REGISTRATION_SALES],
  "B Business Reasons": [DEPARTMENTS.REGISTRATION_SALES],
  "C Legal Reasons": [DEPARTMENTS.LEGAL],
  "D Engineering & Site Issues": [DEPARTMENTS.PROPERTIES, DEPARTMENTS.FIELD_OPERATIONS],
  "E Customer Dissatisfaction": [DEPARTMENTS.REGISTRATION_SALES],
  "F Financial Reasons": [DEPARTMENTS.REGISTRATION_SALES],
  "G Registration Reasons": [DEPARTMENTS.REGISTRATION_SALES],
  "H Management Decisions": [],
  "I Force Majeure / External Events": [],
};

export const canVerifyReallocationReason = (user, category) => {
  if (isMasterAdmin(user)) return true;
  if (!isStaff(user)) return false;
  if (category === undefined) return REALLOCATION_RAISE_DEPARTMENTS.includes(user?.department);
  return (VERIFYING_DEPARTMENTS_BY_CATEGORY[category] || []).includes(user?.department);
};

/** Which departments may verify a given category — used to explain a disabled Verify button. */
export const verifyingDepartmentsFor = (category) =>
  VERIFYING_DEPARTMENTS_BY_CATEGORY[category] || [];

/** Recording customer consent — Registration cum Sales or Customer Support only. */
export const canRecordReallocationConsent = (user) =>
  isMasterAdmin(user) ||
  [DEPARTMENTS.REGISTRATION_SALES, DEPARTMENTS.CUSTOMER_SUPPORT].includes(user?.department);

/** Execute and settle — Registration cum Sales alone, because both write into Module 8. */
export const canExecuteReallocation = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.REGISTRATION_SALES;

/** Lifting a disposition hold on the vacated plot — Properties or Registration cum Sales. */
export const canLiftDisposition = (user) =>
  isMasterAdmin(user) ||
  [DEPARTMENTS.PROPERTIES, DEPARTMENTS.REGISTRATION_SALES].includes(user?.department);

/** Approve and reject are masterOnlyGate — the only management decision points in the flow. */
export const canDecideReallocation = (user) => isMasterAdmin(user);

/** The reallocation configuration is masterOnlyGate. */
export const canManageReallocationConfig = (user) => isMasterAdmin(user);

/**
 * staffReadGate on the list and the disposition register; tieredReadGate on the detail — then
 * `reallocationService.resolveReadTier` grants FULL to Sales, SUPPORT to Customer Support, RESTRICTED
 * to Properties/Field Operations/Legal, and 403s the rest. WAS `isStaff(user)`.
 */
export const canViewReallocations = (user) => hasOperatingDepartment(user);

/**
 * PART 9 FINAL (2026-10-11; MDS CS6 §50.7) — Reallocation Registration: Registration & Sales's registration work for a
 * reallocation's replacement, and the Master Admin's read. `GET /cs-reallocations/registrations` refuses everyone else (403).
 */
export const canViewReallocationRegistrations = (user) =>
  isMasterAdmin(user) || (isStaff(user) && user?.department === DEPARTMENTS.REGISTRATION_SALES);

/**
 * Whether THIS caller may approve THIS reallocation.
 *
 * `reallocationService.approveReallocation`: "The verifier of a reallocation may not also approve
 * it." The route cannot express this — it depends on the record — so the designation gate passes and
 * the service refuses. Mirrored here so the Master Admin who verified sees the reason instead of a
 * button that always 409s. The server remains the authority.
 */
export const canApproveThisReallocation = (user, reallocation) => {
  if (!canDecideReallocation(user) || !reallocation) return false;
  const verifier = reallocation.verification?.verifiedBy;
  if (!verifier) return true;
  return String(verifier) !== String(user._id);
};

// ─────────────────────────────────────────────────────────────────────────────
// Module 10 — Customer Services
// ─────────────────────────────────────────────────────────────────────────────
// Module 10's route gates are DELIBERATELY WIDE, because four of them are reachable by a CUSTOMER and
// a customer has neither department nor designation. The real narrowing is `resolveReadTier` and the
// per-operation authority checks in `serviceRequestService`. The predicates below mirror the SERVICE,
// not the route — mirroring the route would offer buttons that 403 on click.
//
// The consequence worth stating: an ADMINISTRATION staff member who is not the Master Admin is
// granted NO read tier at all and receives 403 on every request read. That is the same deny-by-default
// posture frozen Modules 7, 8 and 9 take, and it is why `canViewServiceRequests` is not `isStaff`.

/**
 * Module 10's own name for the same set. Kept as an alias rather than a second list so the two can
 * never drift apart; `OPERATING_DEPARTMENTS` above carries the reasoning for all four modules.
 */
export const SERVICE_OPERATING_DEPARTMENTS = OPERATING_DEPARTMENTS;

/**
 * `resolveReadTier` grants FULL to the Master Admin and to the Customer Support DEPARTMENT ADMIN;
 * they see every request and all of its content.
 *
 * ── PART 3 §1 · THE CUSTOMER SUPPORT EMPLOYEE IS NO LONGER FULL TIER ────────
 * The company ruled that a Customer Support EMPLOYEE reads only the requests assigned to them, so
 * `serviceRequestService.resolveReadTier` now hands that actor the EMPLOYEE tier. This predicate
 * mirrors it — as every predicate in this file mirrors the service rather than the route — because
 * a stale mirror would offer a Customer Support employee the department-wide controls (the CSV
 * export, the act-on-anything buttons) that the server now refuses.
 */
export const hasFullServiceReadTier = (user) =>
  isMasterAdmin(user)
  || (user?.department === DEPARTMENTS.CUSTOMER_SUPPORT && isDepartmentAdmin(user));

/**
 * PART 3 §1 — a Customer Support employee's service-request reach: their own assigned work.
 * Mirrors `READ_TIER.EMPLOYEE`. Exported so a page can say "this is your worklist" rather than
 * re-deriving the rule.
 */
export const hasAssignedOnlyServiceReadTier = (user) =>
  !isMasterAdmin(user)
  && user?.department === DEPARTMENTS.CUSTOMER_SUPPORT
  && !isDepartmentAdmin(user);

/**
 * The list, the detail and the SLA read. Administration staff who are not the Master Admin have no
 * tier and are refused, so they are not shown the area at all.
 */
export const canViewServiceRequests = (user) =>
  isMasterAdmin(user) || (isStaff(user) && SERVICE_OPERATING_DEPARTMENTS.includes(user?.department));

/**
 * The catalogue is a WIDER read than the requests themselves: `getServiceCatalogue` only refuses a
 * customer, so every staff designation and department may read it — including Administration. It
 * carries routing and classification, never a customer's data.
 */
export const canViewServiceCatalogue = (user) => isStaff(user);

const RAISE_SERVICE_DEPARTMENTS = [DEPARTMENTS.CUSTOMER_SUPPORT, DEPARTMENTS.REGISTRATION_SALES];

/** The one type whose subject is an EMPLOYEE — visible to, and triaged by, a narrower set. */
const SERVICE_RESTRICTED_TYPES = ["Staff/Sales Complaint"];

/**
 * `assertCanRaise` — among staff, only Customer Support (which holds the intake franchise) and
 * Registration cum Sales open a request. Legal, Properties and Field Operations RECEIVE requests and
 * do not open them.
 */
export const canRaiseServiceRequest = (user) =>
  isMasterAdmin(user) || RAISE_SERVICE_DEPARTMENTS.includes(user?.department);

/**
 * `assertCanTriage` — assignment, priority and the due date.
 *
 * Three rules compose here, and the order matters:
 *   1. a customer never triages;
 *   2. a STAFF/SALES COMPLAINT may be triaged by the MASTER ADMIN ALONE, because it names an
 *      employee and Module 10 stores no "complained-about employee" field with which to exclude that
 *      person's department mechanically;
 *   3. otherwise the Master Admin and Customer Support may route anywhere, and a DEPARTMENT ADMIN may
 *      assign only WITHIN THEIR OWN DEPARTMENT. An Employee or Lawyer does not triage at all.
 *
 * `targetDepartment` is the department being assigned TO. Omit it to ask the weaker question "could
 * this person triage anything at all", which is the right question for showing an Assign link.
 */
export const canTriageServiceRequest = (user, request, targetDepartment) => {
  if (!isStaff(user)) return false;
  if (request && SERVICE_RESTRICTED_TYPES.includes(request.type)) return isMasterAdmin(user);
  if (isMasterAdmin(user)) return true;
  if (user?.department === DEPARTMENTS.CUSTOMER_SUPPORT) return true;
  if (!isDepartmentAdmin(user)) return false;
  return targetDepartment === undefined || targetDepartment === user?.department;
};

/**
 * Start, wait, hold, escalate, coordinate, request documents and resolve — the five operating
 * departments, plus the Master Admin override. Visibility is a SEPARATE question: see
 * `canActOnThisServiceRequest`.
 */
export const canWorkServiceRequest = (user) =>
  isMasterAdmin(user) || SERVICE_OPERATING_DEPARTMENTS.includes(user?.department);

/**
 * Whether this caller can reach THIS request at all.
 *
 * `loadServiceRequestForActor` refuses a department-tier caller whose department is neither assigned
 * to nor coordinated with the request — with 404, not 403. Mirrored so a Legal member browsing a
 * Properties request sees it read-only rather than a row of buttons that all fail.
 */
export const canActOnThisServiceRequest = (user, request) => {
  if (!canWorkServiceRequest(user) || !request) return false;
  if (hasFullServiceReadTier(user)) return true;
  if (SERVICE_RESTRICTED_TYPES.includes(request.type)) return false;
  // PART 3 §1 — a Customer Support employee acts on the requests ASSIGNED TO THEM, and on nothing
  // else. Checked BEFORE the department test below, because that test would otherwise pass for
  // every Customer Support request and hand this actor buttons the server answers 404.
  // `String(...)` on both sides: `assignedEmployee` arrives as an id string from the API while
  // `user._id` may be either, and `===` between the two forms is silently false.
  if (hasAssignedOnlyServiceReadTier(user)) {
    return Boolean(request.assignedEmployee)
      && String(request.assignedEmployee) === String(user?._id);
  }
  return (
    request.assignedDepartment === user?.department ||
    (request.coordination || []).some((entry) => entry.department === user?.department)
  );
};

/**
 * `POST /:id/execute` is `requireDepartment(Registration cum Sales)` at the route AND
 * `assertCanExecute(actingUser, executingDepartment)` in the service. Both must pass, so a Legal
 * member can never execute even a Legal-executing type — and every Class 1 row in the frozen seed is
 * in any case seeded to Registration cum Sales.
 */
/**
 * Whether this caller may record an owner's consent on a service request.
 *
 * Mirrors the router's `consentGate` exactly — the two customer-facing departments, plus the Master
 * Admin override — because consent is ATTESTED by the staff member who obtained it (Resolution 4
 * forbids customer self-service). Cosmetic, as every predicate here is: the service re-checks the
 * staff tier and this caller's visibility of the request on every call.
 */
export const canRecordServiceRequestConsent = (user) =>
  isMasterAdmin(user) ||
  user?.department === DEPARTMENTS.REGISTRATION_SALES ||
  user?.department === DEPARTMENTS.CUSTOMER_SUPPORT;

export const canExecuteServiceRequest = (user, executingDepartment) => {
  if (isMasterAdmin(user)) return true;
  if (user?.department !== DEPARTMENTS.REGISTRATION_SALES) return false;
  return executingDepartment === undefined || executingDepartment === DEPARTMENTS.REGISTRATION_SALES;
};

/**
 * Close, reopen and staff cancellation — the INTAKE authority. Customer Support holds the
 * service-request franchise; an executing department resolves its own work but does not end the
 * request.
 */
export const canCloseServiceRequest = (user) =>
  isMasterAdmin(user) || user?.department === DEPARTMENTS.CUSTOMER_SUPPORT;

/** The catalogue and the policy configuration are masterOnlyGate. */
export const canManageServiceRequestConfig = (user) => isMasterAdmin(user);

// ─────────────────────────────────────────────────────────────────────────────
// Module 11 — Documents
// ─────────────────────────────────────────────────────────────────────────────
// Module 11's read surface is WIDER than Module 10's and its write surface is narrower.
//
//   • Reading is open to EVERY staff designation and department, including Administration:
//     `assertStaffReader` refuses only a customer. There is no department read tier here.
//   • Uploading is likewise open to every staff account: `assertStaffWriter` refuses only a customer,
//     because no frozen document excludes any staff department from uploading.
//   • ACTING on a document — remark, correction, expiry, verification — is confined to the five
//     operating departments by the route, and verification is narrowed much further in the service.
//   • The SENSITIVITY tier cuts across all of it: an identity or financial document is readable by
//     the Master Admin and by the department configured to verify that type. While no verifying
//     department is configured — the seeded state — that means the MASTER ADMIN ALONE.

/** `staffActionGate` / `verifyGate` — the five operating departments. */
export const DOCUMENT_ACTION_DEPARTMENTS = [
  DEPARTMENTS.CUSTOMER_SUPPORT,
  DEPARTMENTS.REGISTRATION_SALES,
  DEPARTMENTS.LEGAL,
  DEPARTMENTS.PROPERTIES,
  DEPARTMENTS.FIELD_OPERATIONS,
];

/** Every document read: the list, the detail, versions, by-entity, completeness, expiry, config. */
export const canViewDocuments = (user) => isStaff(user);

/**
 * `assertStaffWriter` — any staff account may request an upload authorization and confirm it.
 * The route describes this as customer-reachable; the service refuses customers, so in V1 the
 * upload flow is staff-only in practice.
 */
export const canUploadDocument = (user) => isStaff(user);

// ── PH4-CORRECTION §5/§7/§8/§33 · THE LEGACY GLOBAL DOCUMENTS MODULE ────────────────────────────
//
// WHAT THIS SEPARATES. `canViewDocuments` above is the CONTEXTUAL document read — the panel on a
// property, venture or plot, a document's own record, its version history. That stays open to every
// staff account and is NOT touched here; §62 keeps those surfaces exactly as they are.
//
// This predicate is a different thing: the COMPANY-WIDE DOCUMENT REGISTER and the screens that only
// exist to serve it — the `/documents` table with its KPI strip and category/type/standing filters,
// the generic `/documents/upload` form that asks the user to pick an arbitrary context and type a
// Record ID and a Document ID, and the Expiry and Integrity boards hung off the same register. §1
// names that whole cluster LEGACY and §5 removes it from the Property product surface.
//
// WHY A PREDICATE RATHER THAN A DELETION. §7 and §33 both say the same thing: another department
// that legitimately owns the route keeps it, and shared infrastructure is not deleted during a
// Property cleanup. `navigationProfiles` shows five profiles that still carry the register row —
// Registration cum Sales, the generic department-admin profile, and the Field Operations, Legal and
// Customer Support employee profiles — so the screens are still someone's operational surface.
// Deleting them would break those departments, which §0-level scope forbids.
//
// WHY THIS IS REMOVAL AND NOT HIDING (§6). §6 rules out "role-based hiding while route remains
// reachable through Property UI". Both halves of that are answered: the route is NOT reachable for a
// Property user — typing `/documents` lands on the dashboard through the project's own unauthorized
// convention (`RoleRoute`'s `<Navigate to="/" replace />`, §7's "existing convention, do not invent a
// new response style") — and no Property surface links to it any more, because the last three
// callers were converted to the contextual upload modal in this same correction. Nothing is hidden
// with CSS, disabled, or left standing behind an invisible link.
//
// Property document work happens where the document lives: the entity panel's own Add Document and
// Upload new version, the request, and the task.
// The Master Admin is named first and unconditionally: their department field is Administration,
// not Properties, so the second clause would already admit them — but their access must not depend
// on a field whose value is incidental to the reason they have it.
// ── FIELD PHASE 2 §11 · FIELD OPERATIONS JOINS THE EXCLUSION ────────────────────────────────────
//
// Phase 1 BD-2 withdrew the `Documents` ROW from both Field profiles. §11 asks for the boundary
// itself, and this is its client half: `documentService.listDocuments` now refuses a Field caller
// 403, and this stops the panel drawing a door onto that refusal.
//
// TWO DEPARTMENTS ARE NOW EXCLUDED FOR THE SAME REASON AND BY THE SAME CLAUSE. Properties was
// excluded by PH4-CORRECTION §5; Field Operations joins it here. Legal and Customer Support
// legitimately depend on the register and are deliberately untouched — this is a Field ruling, not
// a global withdrawal, and widening it further is not this phase's to decide.
//
// THE CONTEXTUAL SURFACES ARE UNAFFECTED. `canViewDocuments`, `canUploadDocument`, `canActOnDocument`
// and `canVerifyDocument` are unchanged, so `/documents/:id`, the version history, the entity panels
// and `AddDocumentModal` all still work for Field — which is where Field's evidence actually lives.
export const canUseGlobalDocumentRegister = (user) =>
  isMasterAdmin(user)
  || (isStaff(user)
    && user?.department !== DEPARTMENTS.PROPERTIES
    && user?.department !== DEPARTMENTS.FIELD_OPERATIONS);

/** Remarks, classification and linkage corrections, and expiry — the five operating departments. */
export const canActOnDocument = (user) =>
  isMasterAdmin(user) || DOCUMENT_ACTION_DEPARTMENTS.includes(user?.department);

/**
 * Whether this caller could verify, reject or require re-upload on THIS document.
 *
 * Four rules compose, and three of them configuration can never override:
 *   1. a customer never verifies;
 *   2. CUSTOMER SUPPORT MAY NEVER APPROVE — a frozen worked example, refused in the service and
 *      refused again when a configuration tries to name it as a verifying department;
 *   3. THE UPLOADER MAY NEVER VERIFY THEIR OWN UPLOAD;
 *   4. otherwise the Master Admin, or the department CONFIGURED to verify this type — and while
 *      none is configured, only the Master Admin.
 *
 * `currentVersion` is the version being judged; omit it to ask the weaker question "could this
 * person verify anything at all", which is the right question for showing the panel.
 */
export const canVerifyDocument = (user, typePolicy, currentVersion) => {
  if (!isStaff(user)) return false;
  if (user?.department === DEPARTMENTS.CUSTOMER_SUPPORT) return false;
  if (
    currentVersion?.uploadedBy &&
    String(currentVersion.uploadedBy) === String(user?._id)
  ) {
    return false;
  }
  if (isMasterAdmin(user)) return true;
  return Boolean(typePolicy?.verifyingDepartment) &&
    user?.department === typePolicy.verifyingDepartment;
};

/** Why the verification panel is closed, in the server's own terms — or null when it is open. */
export const verificationBlocker = (user, typePolicy, currentVersion) => {
  if (!isStaff(user)) return "Only staff may record a verification decision.";
  if (user?.department === DEPARTMENTS.CUSTOMER_SUPPORT) {
    return "Customer Support may not approve documents — a departmental boundary that configuration cannot override.";
  }
  if (
    currentVersion?.uploadedBy &&
    String(currentVersion.uploadedBy) === String(user?._id)
  ) {
    return "You uploaded this version, and the uploader may not verify their own upload. Another reviewer must judge it.";
  }
  if (isMasterAdmin(user)) return null;
  if (!typePolicy?.verifyingDepartment) {
    return (
      "No verifying department is configured for this document type, so only the Master Admin can " +
      "record a decision. A Master Admin sets the verifying department per type on the document " +
      "settings page."
    );
  }
  if (user?.department !== typePolicy.verifyingDepartment) {
    return `Only ${typePolicy.verifyingDepartment} or the Master Admin may verify this document type.`;
  }
  return null;
};

/**
 * `assertMayReadSensitivity` — whether this caller may read a document of this sensitivity.
 *
 * An identity or financial document is readable by the Master Admin and by the configured verifying
 * department, and by nobody else. The "department owning the linked entity" tier the design names is
 * NOT implemented in the backend — no frozen entity-kind → department map exists — so while the
 * verifying department stays unconfigured, a Sensitive document is readable by the Master Admin
 * alone. Rows the caller may not read are removed from a list entirely, not redacted.
 */
export const canReadSensitiveDocument = (user, sensitivity, verifyingDepartment) => {
  if (sensitivity !== "Sensitive") return true;
  if (isMasterAdmin(user)) return true;
  return Boolean(verifyingDepartment) && user?.department === verifyingDepartment;
};

/** The cross-cutting integrity reads — Master Admin or Department Admin. */
export const canViewDocumentIntegrity = (user) => isAdmin(user);

/** The catalogue row and expected-set maintenance are masterOnlyGate. */
export const canManageDocumentConfig = (user) => isMasterAdmin(user);

/**
 * CS-5B (owner-approved 2026-09-28) — who may OPEN /documents/catalogue/reusability. The Master Admin, or any Department
 * Admin: the page then shows only the types that person governs (utils/reusabilityGovernance.js mirrors the server
 * rule), and a Department Admin who governs none — the Administration Admin today — reads "Your department does not
 * govern the reusability of any document type." Employees, Lawyers and customers get the standard access-denied page.
 */
export const canOpenDocumentReusability = (user) => isMasterAdmin(user) || isDepartmentAdmin(user);

// ── P5 (MDS P5-D4/D5/D6, owner 2026-09-21) · DEPARTMENT REQUIREMENT EXPERIENCES ─────────────────
// ONE requirement engine (`documentConfig.expectedDocumentSets`), three department doors. The Property
// Admin and the R&S Admin AUTHOR within their own scope (add, version, request retirement — never retire
// directly); the Legal Admin reads the Legal-verification rows. Each mirrors the server's scope rule in
// `documentCatalogService` (`requirementAuthorScopeFor`), which is the real gate. The Master Admin keeps
// `/documents/settings` and is deliberately NOT admitted here — Master work is deferred in Part 5.
export const PROPERTY_REQUIREMENT_KINDS = ["Property", "Venture", "Plot"];
export const REGISTRATION_REQUIREMENT_KINDS = ["SalesRegistration"];

export const canManagePropertyRequirements = (user) =>
  isDepartmentAdmin(user) && user?.department === DEPARTMENTS.PROPERTIES;

export const canManageRegistrationRequirements = (user) =>
  isDepartmentAdmin(user) && user?.department === DEPARTMENTS.REGISTRATION_SALES;

export const canReadLegalRequirements = (user) =>
  isDepartmentAdmin(user) && user?.department === DEPARTMENTS.LEGAL;

// ─────────────────────────────────────────────────────────────────────────────
// Module 12 — Notifications
// ─────────────────────────────────────────────────────────────────────────────
// The simplest permission model in the project, and deliberately so:
//
//   • THE INBOX AND PREFERENCES ARE PERSONAL FOR EVERY ROLE WITHOUT EXCEPTION. There is no
//     department scope, no admin override and no way to read or alter another person's inbox — a
//     Master Admin may READ delivery status but may never mark someone else's notification read,
//     because read status must stay auditable and reversing it would falsify it.
//   • EVERYTHING ELSE IS MASTER ADMIN ONLY. A Department Admin is scoped to employees, tasks and
//     performance — not to other people's inboxes — so they are given NO notification scope at all.
//     That is a deliberate ruling, not an oversight, and it is why `isAdmin` is not used here.
//   • The retry route's designation gate is only its outer half: the service requires a Master
//     Admin, so a non-Master staff caller reaches it and receives 403. The predicate below mirrors
//     the SERVICE, which is the effective rule.

/** The inbox, the unread count and the preference surface. Every authenticated account has one. */
export const canViewOwnNotifications = (user) => Boolean(user);

/** Preferences are personal for every role — there is no administrative path to another user's. */
export const canManageOwnNotificationPreferences = (user) => Boolean(user);

/** The catalogue, the configuration, all three oversight reads and the Module 13 contract read. */
export const canViewNotificationOversight = (user) => isMasterAdmin(user);

/** Catalogue routing and configuration maintenance. */
export const canManageNotificationConfig = (user) => isMasterAdmin(user);

/**
 * The bounded manual re-queue of a Failed channel.
 *
 * The ROUTE admits any staff designation; the SERVICE requires a Master Admin. Mirroring the service
 * means a Department Admin is not offered a button that always 403s.
 */
export const canRetryNotificationDispatch = (user) => isMasterAdmin(user);

// ─────────────────────────────────────────────────────────────────────────────
// Module 13 — Audit & Timeline
// ─────────────────────────────────────────────────────────────────────────────
// Four tiers, and the difference between the first two is what makes this module unusual:
//
//   • A DEPARTMENT ADMIN IS SCOPED BY THE ACTOR'S DEPARTMENT, NOT THE ENTITY'S. They see what their
//     own people did, anywhere in the system — not everything that happened to their department's
//     records. An entry outside that scope answers 404, not 403.
//   • A DEPARTMENT ADMIN WITH NO DEPARTMENT SEES NOTHING, rather than everything. An absent
//     department is a data defect, and the safe reading of a defect is the empty one.
//   • EVERY STAFF DESIGNATION may read their OWN activity — the one unprivileged read here.
//   • ALL OVERSIGHT IS MASTER ADMIN ONLY: the catalogue, the configuration, unclassified entries,
//     schedule runs, stranded events and the schedule contract. A Department Admin has no scope over
//     any of them.
//   • A CUSTOMER HAS NO AUDIT ACCESS IN ANY FORM. The one customer-reachable route is a separate,
//     allow-listed milestone projection that ships empty.

/** The audit trail, one entry, the entity timeline and the recent-activity feed. */
export const canViewAuditTrail = (user) => isAdmin(user);

/**
 * Whether this caller sees the WHOLE trail or only their own department's actors.
 * Used to explain the scope on screen rather than leaving a short list unexplained.
 */
export const hasUnscopedAuditRead = (user) => isMasterAdmin(user);

/** Own activity — every staff designation, and no customer. */
export const canViewOwnActivity = (user) => isStaff(user);

/** Unclassified entries, schedule runs, stranded events and the schedule contract. */
export const canViewAuditOversight = (user) => isMasterAdmin(user);

/** The classification catalogue and the module configuration. */
export const canManageAuditConfig = (user) => isMasterAdmin(user);

// ── Module 13T · Task & Assignment Management ───────────────────────────────
// Mirrors `functions/src/routers/taskRoutes.js` and `taskService.js`. Cosmetic, like everything else
// in this file: the service re-checks the actor, the department, the assignment and the task's status
// on every call, and answers 404 (never 403) for another department's task so an id cannot be probed.
//
// THE ONE RULE WORTH STATING TWICE: an EMPLOYEE never assigns or reassigns. They may only REQUEST a
// reassignment, which the Department Admin of that task's department then decides. There is
// deliberately no predicate here that would render a "reassign to employee X" control for them.

/** The Tasks area at all. Customers are refused by the router; this hides it from them too. */
export const canViewTasks = (user) => isStaff(user);

/**
 * Assign, reassign, take the task, decide a reassignment request, change priority, revise the
 * deadline, cancel, and view the department queue and its eligible employees.
 *
 * A Department Admin holds this within their OWN department; the Master Admin holds it everywhere
 * (business/02 §5, fully audited). `taskDepartment` is the department being acted on — omit it to ask
 * the weaker question "could this person administer anything at all", which is the right question for
 * showing a nav item.
 */
export const canAdministerTasks = (user, taskDepartment) => {
  if (!isStaff(user)) return false;
  if (isMasterAdmin(user)) return true;
  if (!isDepartmentAdmin(user)) return false;
  // PART 2 (browser-found): the sidebar calls every row predicate as `can(user, context)` (CS-5B), and that context
  // OBJECT was read here as a task department — so every Department Admin's Queue, Completion Reviews, Reassignment
  // and Extension rows were hidden. Only a department NAME narrows; anything else means "no particular task".
  const department = typeof taskDepartment === "string" ? taskDepartment : undefined;
  return department === undefined || department === user?.department;
};

/**
 * `POST /tasks` (EP-01). Master Admin in any department; Department Admin in their OWN.
 *
 * WIDENED by owner decision D10 (2026-08-11), which reversed D4. Frozen `business/16 §3` has always
 * read "Tasks are assigned by the relevant Department Admin (or Master Admin)" — the code was
 * stricter than the document, and the service now enforces `canAdministerDepartment`. This asks the
 * weaker "could this person create anything at all" question, which is the right one for showing a
 * nav item or guarding the route; the service re-checks the department when the task is created.
 */
export const canCreateDepartmentTask = (user, taskDepartment) =>
  canAdministerTasks(user, taskDepartment);

/**
 * Whether this caller may assign an employee to THIS task — the same question the service asks, so
 * the button is not rendered where it would 403 or 404.
 */
export const canAssignTask = (user, task) =>
  !!task && canAdministerTasks(user, task.assignedDepartment);

/**
 * Start, complete and record progress — THE CURRENT ASSIGNEE ALONE, whatever their designation.
 *
 * A Department Admin who has taken a task passes here as its OWNER, not as an administrator; one who
 * has not does NOT pass, because administering a task is not the same as doing it.
 */
export const canWorkTask = (user, task) =>
  !!user && !!task && !!task.assignedEmployee &&
  String(task.assignedEmployee) === String(user._id) &&
  !["Completed", "Cancelled"].includes(task.status);

/**
 * `POST /:id/reassignment-requests` — the assignee's ONLY route out of a task they cannot complete.
 *
 * Refused while another request is still pending (409), so the control is hidden in that case rather
 * than offered and rejected.
 */
export const canRequestReassignment = (user, task) =>
  canWorkTask(user, task) &&
  !(task.reassignmentRequests || []).some((r) => r.status === "Pending");

/** Whether a pending reassignment request on this task is this caller's to decide. */
export const canDecideReassignment = (user, task) =>
  canAdministerTasks(user, task?.assignedDepartment) &&
  (task?.reassignmentRequests || []).some((r) => r.status === "Pending");

/** `POST /:id/submit` — the assignee hands finished work back. Never a completion. */
export const canSubmitTask = (user, task) =>
  canWorkTask(user, task) && task?.status === "In Progress";

/** `POST /:id/review` — approve or send back. The task's Department Admin, or the Master Admin. */
export const canReviewTask = (user, task) =>
  canAdministerTasks(user, task?.assignedDepartment) && task?.status === "Submitted";

/**
 * `POST /:id/reopen` — the ONE carve-out in business/16 §3's "a completed task is final" rule
 * (amendment A4, 2026-08-19, owner decision D8-C).
 *
 * THREE CONDITIONS, ALL REQUIRED, AND THE SERVER ENFORCES EACH INDEPENDENTLY. The task must be
 * `Completed`, it must be a **Legal** task, and the caller must be the Legal Department Admin or the
 * Master Admin. This mirrors the refusal; it never replaces it — a caller who reaches the endpoint by
 * any other route is refused there, not here.
 *
 * DELIBERATELY NOT GENERALISED TO `assignedDepartment`. Reopening is not a property of being a
 * Department Admin; it is a property of Legal, granted to Legal by an explicit amendment. Writing it
 * as `canAdministerTasks(user, task.assignedDepartment)` would silently hand every other department
 * the same power the amendment withheld from them.
 */
export const canReopenTask = (user, task) =>
  task?.status === "Completed" &&
  task?.assignedDepartment === DEPARTMENTS.LEGAL &&
  canAdministerTasks(user, DEPARTMENTS.LEGAL);

/*
 * NO `canClaimTask` (business/16 §3 - A3, 2026-08-12). It gated a Claim button against the three
 * service conditions for `POST /:id/claim`. Self-claim is retired, the endpoint is gone, and there is
 * no successor: an employee receives work only through a Department Admin's explicit assignment.
 */

/** `POST /:id/extension-requests` — assignee only, and hidden while one is already pending. */
export const canRequestExtension = (user, task) =>
  canWorkTask(user, task) &&
  !(task.extensionRequests || []).some((r) => r.status === "Pending");

/** Whether a pending extension request on this task is this caller's to decide. */
export const canDecideExtension = (user, task) =>
  canAdministerTasks(user, task?.assignedDepartment) &&
  (task?.extensionRequests || []).some((r) => r.status === "Pending");

// ─────────────────────────────────────────────────────────────────────────────
// Administration — the Master Admin's organisation-control area (Part 1)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The Administration CONTROL surfaces: the department directory, the Work Tracking boards and the
 * System configuration hub.
 *
 * ── WHY IT IS ITS OWN NAME AND NOT `isMasterAdmin` INLINE ───────────────────
 * Administration is not an operating department (Part 1 §1): it is the organisation-control area,
 * and exactly one actor holds it. Naming the CAPABILITY rather than testing the designation at each
 * call site means that when Part 2 gives one of these boards a narrower or a wider tier, one
 * predicate changes instead of a dozen route guards being audited by hand.
 *
 * ── IT IS COSMETIC, LIKE EVERY PREDICATE IN THIS FILE (Part 1 §5) ───────────
 * It hides doors; it does not lock them. The Administration pages read through EXISTING endpoints
 * (`GET /users`, `GET /audit`, `GET /tasks`), every one of which re-checks the caller server-side and
 * scopes the answer to their department. No new backend permission, resource or route was introduced
 * for Administration, and none of the frozen `requireDesignation` chains was weakened: an operating
 * department user who types `/administration/departments` is refused the DATA by the API even in the
 * hypothetical case that they reached the page.
 */
export const canAdministerOrganisation = (user) => isMasterAdmin(user);

/**
 * The Administration DEPARTMENT's admin — a different identity from the Master Admin, whose record also says
 * "Administration" (owner department-structure ruling, 2026-10-14: Administration has exactly one Department Admin and
 * no employees). Decided by designation AND department, so the Master Admin never matches. Used to choose this
 * account's own landing page and password-reset data sources; it grants nothing — every API re-checks the caller.
 */
export const isAdministrationDepartmentAdmin = (user) =>
  isStaff(user) && isDepartmentAdmin(user) && user?.department === DEPARTMENTS.ADMINISTRATION;

/**
 * A6 (business/02, owner-approved 2026-10-14) — READ-ONLY organisation monitoring: the Master Admin, or the
 * Administration Department Admin. Mirrors the server's `monitoringGate` + `assertCanMonitorOrganisation`, which are the
 * real boundary. Decisions, Exceptions, Configuration and every other Master Admin-only surface keep
 * `canAdministerOrganisation`.
 */
export const canMonitorOrganisation = (user) => isMasterAdmin(user) || isAdministrationDepartmentAdmin(user);
